import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Check, RefreshCw, Lock, Unlock, X, ArrowRightLeft, Ban, ChevronDown, ChevronUp, Scale, MoreHorizontal, AlertTriangle, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import DraftSwapPicker from '../components/DraftSwapPicker';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { smartFillAPI, mealAPI } from '../utils/api';
import * as pairLogic from './planReviewPairLogic';
import { friendlyRegenerateDayFailure } from './planReviewRegenerateDayFailure';
import { isPlanReviewNavStateUsable } from './planReviewNavStateGuard';
import { FIX_MODE_KEY, setFixModeAdvancePending } from '../utils/fixModeAdvanceSignal';
import { safeReturnPath, finishFlow, buildUrl, currentPath, startFlowState } from '../utils/planLocation';
import { startDraftSwap, readDraftSwap, clearDraftSwap } from '../utils/draftSwapHandoff';
import { recordSwapTasteSignals, recordOrderedRegenSignals, TASTE_ACTIONS, recordTasteSignal } from '../utils/tasteSignals';
import { getStatusDisplay, getMacroBarColor } from '../utils/macroStatus';
import { toast } from 'sonner';

function formatDate(dateStr) {
  var d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

var SLOT_ORDER = ['Breakfast', 'Lunch', 'Dinner', 'Snack'];
var PRIMARY_SLOTS = ['Breakfast', 'Lunch', 'Dinner'];

function normalizeSlot(v) {
  if (!v) return 'Snack';
  var s = String(v).trim();
  if (SLOT_ORDER.indexOf(s) >= 0) return s;
  var map = { breakfast: 'Breakfast', lunch: 'Lunch', dinner: 'Dinner', snack: 'Snack', supper: 'Dinner', brunch: 'Lunch' };
  return map[s.toLowerCase()] || 'Snack';
}

// Defensive guard when merging regenerated rows: a single date must never end
// up with two primary (B/L/D) slots. Keeps the FIRST occurrence (kept/locked/
// leftover rows are concatenated first). Backend remains the source of truth.
// Logged (`is_logged`) rows are food the user already ate: they are never
// dropped and never claim the slot (e.g. a logged Lunch beside a linked
// leftover Lunch keeps both). The backend already refuses to generate a new
// primary meal into a slot a locked logged row occupies.
function dedupePrimarySlots(rows, date) {
  var seen = {};
  return rows.filter(function (r) {
    if (r.date !== date) return true;
    if (r.is_logged) return true;
    var slot = normalizeSlot(r.meal_type);
    if (PRIMARY_SLOTS.indexOf(slot) === -1) return true;
    if (seen[slot]) return false;
    seen[slot] = true;
    return true;
  });
}

// Map structured backend error codes to safe, user-friendly messages. Never
// surfaces raw server exception text.
function friendlyPlanError(err, fallback) {
  try {
    var detail = err && err.response && err.response.data && err.response.data.detail;
    var code = detail && typeof detail === 'object' ? detail.error : null;
    var map = {
      duplicate_slot: 'This day ended up with two of the same meal type. Please regenerate this day.',
      duplicate_meal: 'This day had a duplicated meal. Please regenerate this day.',
      invalid_leftover_group: 'The leftover pairing for this day is incomplete. Please regenerate this day.',
      incomplete_pair_context: 'Please reload the plan and try again — the leftover pair information was incomplete.',
      pair_reroll_not_allowed: 'To change a leftover pair, use Replace Both.',
      incomplete_dates: 'Some selected days have no meals yet. Regenerate or remove them before saving.',
      generated_day_off_target: "This plan isn't fully On Track. Use Regenerate Day on the flagged day before saving.",
      incomplete_completion_available: "This day can still be completed On Track. Use Regenerate Day before saving.",
    };
    if (code && map[code]) return map[code];
  } catch (e) { /* fall through to fallback */ }
  return fallback;
}

export default function PlanReview() {
  var navigate = useNavigate();
  var location = useLocation();
  var draftData = location.state || {};

  // Returning from the Plan Swap browser (Meal Review Swap): this Review's
  // working draft as it was when Swap opened, plus the chosen meal (if any).
  var [restored] = useState(function () {
    var record = readDraftSwap();
    return record && record.state && record.reviewKey === location.key ? record : null;
  });
  var restoredState = restored ? restored.state : null;

  var [plans, setPlans] = useState(function () {
    return restoredState ? restoredState.plans : pairLogic.withDraftRowKeys(draftData.added_plans || []);
  });
  // draftData.daily_target/goal always come from the generation response,
  // which (as of the backend's fail-closed goal/targets gate) only ever
  // succeeds for a profile with real, complete targets and an explicit goal.
  // A missing value here means a malformed navigation payload reached this
  // screen — never rendered with a fabricated 2000/150/200/67 target or an
  // assumed "Maintain" goal as if it were the user's own; the guard below
  // fails safely instead (see AGENTS.md pre-launch audit nutrition-readiness
  // follow-up).
  var [dailyTarget] = useState(draftData.daily_target || null);
  var [dates] = useState(draftData.dates || []);
  var [generatedReplacePlanIds] = useState(draftData.replace_plan_ids || []);
  // Plus any kept saved meal a Meal Review Swap replaced (see applySingleSwapToPlans).
  var replacePlanIds = pairLogic.draftReplacePlanIds(generatedReplacePlanIds, plans);
  var userGoal = draftData.goal || null;

  // Issue 2: per-date compliance signal from the backend. Each entry:
  //   { date, compliant, compliance_dist, round_used, retry_attempt }
  // The pill renders ONLY when compliant === false — i.e. the backend
  // already exhausted its retry attempts and still couldn't land an On
  // Track day. We never show it speculatively; otherwise the user would
  // see it on days the backend could still improve.
  // Backend overrides this when a regen-day retry succeeds, so locally
  // mutating it on `handleRegenerateDay` is what keeps the pill in sync.
  var [complianceByDate, setComplianceByDate] = useState(function () {
    if (restoredState) return restoredState.complianceByDate || {};
    var map = {};
    (draftData.daily_results || []).forEach(function (r) {
      if (r && r.date) {
        map[r.date] = {
          compliant: r.compliant !== false, // default true if missing
          compliance_dist: r.compliance_dist || 0,
          round_used: r.round_used || 0,
          retry_attempt: r.retry_attempt || 0,
          // 6C: authoritative planner terminal status + reason.
          status: r.status || (r.compliant === false ? 'off_track' : 'on_track'),
          reason: r.reason || null,
        };
      }
    });
    return map;
  });

  var [expandedDays, setExpandedDays] = useState(function () {
    if (restoredState) return restoredState.expandedDays || {};
    var init = {};
    (draftData.dates || []).forEach(function (d) { init[d] = true; });
    return init;
  });
  var [lockedMeals, setLockedMeals] = useState(function () {
    return (restoredState && restoredState.lockedMeals) || {};
  });
  var [excludedMealIds, setExcludedMealIds] = useState(function () {
    return (restoredState && restoredState.excludedMealIds) || [];
  });
  var [saving, setSaving] = useState(false);
  // Stable idempotency key for THIS save attempt. Generated once and reused
  // across network retries so a save that committed but whose response was
  // interrupted is recognised as a replay (never applied twice). Cleared only
  // after a confirmed success so the next distinct save gets a fresh key.
  var [saveOpId, setSaveOpId] = useState(null);
  var [regeneratingDay, setRegeneratingDay] = useState(null);
  var [swapSlot, setSwapSlot] = useState(null);
  var [swapAlternatives, setSwapAlternatives] = useState([]);
  var [loadingAlternatives, setLoadingAlternatives] = useState(false);
  var [evaluatingDraft, setEvaluatingDraft] = useState(false);
  var evaluationSequence = useRef(0);
  var restoreHandledRef = useRef(false);

  // Meal Review Swap return: replace EXACTLY the swapped draft row with the
  // chosen meal (nothing was written anywhere), then re-check the draft like
  // every other draft edit. Back without a choice keeps the draft as it was.
  useEffect(function () {
    if (!restored || restoreHandledRef.current) return;
    restoreHandledRef.current = true;
    clearDraftSwap();
    var pick = restored.pick;
    if (!pick) return;
    // (Its taste signal was recorded by performSwap when the choice was made.)
    var swappedPlans = pairLogic.applySingleSwapToPlans(restoredState.plans, pick.row_key, pick);
    setPlans(swappedPlans);
    reevaluateDraft(swappedPlans);
    toast.success('Meal swapped');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  var getMealsByDate = useCallback(function (date) {
    return plans.filter(function (p) { return p.date === date; })
      .sort(function (a, b) { return SLOT_ORDER.indexOf(a.meal_type) - SLOT_ORDER.indexOf(b.meal_type); });
  }, [plans]);

  var getDayTotals = useCallback(function (date) {
    var meals = getMealsByDate(date);
    var t = { calories: 0, protein: 0, carbs: 0, fat: 0 };
    meals.forEach(function (m) {
      t.calories += m.calories || 0;
      t.protein += m.protein || 0;
      t.carbs += m.carbs || 0;
      t.fat += m.fat || 0;
    });
    return t;
  }, [getMealsByDate]);

  // Malformed navigation state (e.g. a stale/incomplete draft, or this route
  // opened directly without going through generation): fail safely with a
  // back action instead of rendering a fabricated target/goal as the user's
  // own (see the dailyTarget/userGoal comment above).
  if (!isPlanReviewNavStateUsable(draftData)) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center gap-4">
        <AlertTriangle size={32} className="text-amber-500" />
        <p className="font-heading font-semibold text-lg">Couldn't load this plan review</p>
        <p className="text-sm text-muted-foreground max-w-xs">
          Something went wrong loading your generated plan. Please go back and try again.
        </p>
        <Button onClick={function () { navigate(-1); }} className="rounded-full" data-testid="plan-review-error-back-btn">
          <ArrowLeft size={16} className="mr-2" />
          Go back
        </Button>
      </div>
    );
  }

  function toggleDay(date) {
    setExpandedDays(function (prev) {
      var next = Object.assign({}, prev);
      next[date] = !next[date];
      return next;
    });
  }

  // Lock state is PAIR-LEVEL for a valid linked leftover pair: locking either
  // member locks both (cook on Day N + leftover on Day N+1), and unlocking
  // either unlocks both. Regular rows stay per-row. This keeps the visual/
  // functional lock consistent with the already-pair-wide Remove/Don't Suggest
  // and prevents breaking a pair through its "unlocked" partner row.
  function _pairMembersFor(date, mealId) {
    return pairLogic.getPairMembers(plans, date, mealId);
  }
  function toggleLock(date, mealId) {
    var members = _pairMembersFor(date, mealId);
    var shouldLock = !pairLogic.isPairLocked(lockedMeals, members);
    setLockedMeals(function (prev) { return pairLogic.applyLockToggle(prev, members, shouldLock); });
  }

  function isLocked(date, mealId) {
    return pairLogic.isPairLocked(lockedMeals, _pairMembersFor(date, mealId));
  }

  // A draft leftover group is "valid" only when it has EXACTLY two members —
  // one cook + one leftover sharing the same recipe. Anything else (single
  // orphan, >2, mixed recipes) is malformed and must be handled via the saved
  // plan's Swap, not the draft pair-swap. Confirm is the final backend guard.
  function getGroupMembers(groupId) {
    return pairLogic.getGroupMembers(plans, groupId);
  }
  function isValidDraftPair(groupId) {
    return pairLogic.isValidDraftPair(plans, groupId);
  }

  async function reevaluateDraft(nextPlans) {
    var seq = ++evaluationSequence.current;
    setEvaluatingDraft(true);
    // Judged on the draft being evaluated (it may not be rendered yet).
    var replacePlanIds = pairLogic.draftReplacePlanIds(generatedReplacePlanIds, nextPlans);
    try {
      var res = await smartFillAPI.evaluateDraft(nextPlans, dates, replacePlanIds);
      if (seq !== evaluationSequence.current) return;
      var next = {};
      (res.data.daily_results || []).forEach(function (r) { next[r.date] = r; });
      setComplianceByDate(next);
    } catch (err) {
      if (seq === evaluationSequence.current) {
        toast.error(friendlyPlanError(err, 'Could not verify the updated draft'));
        var failed = {};
        dates.forEach(function (date) { failed[date] = { status: 'off_track', compliant: false, reason: 'evaluation_failed' }; });
        setComplianceByDate(failed);
      }
    } finally {
      if (seq === evaluationSequence.current) setEvaluatingDraft(false);
    }
  }

  function handleRemoveMeal(date, mealId) {
    // Linked rows are removed as a PAIR (never leave an orphan). Find the row's
    // group and drop every member together.
    // NOTE (P8b-B1): this function records NO weak taste signal — Don't Suggest
    // also calls it and must stay silent (strong exclusion subsumes it). Weak
    // 'remove' evidence is recorded only by handleDirectRemove (the Remove button).
    var next = pairLogic.removePairFromPlans(plans, date, mealId);
    setPlans(next);
    reevaluateDraft(next);
  }

  function handleDirectRemove(date, mealId) {
    // Direct Remove button => weak negative for this recipe family (one signal
    // even when a linked pair is removed, since both rows share the recipe).
    recordTasteSignal(mealId, TASTE_ACTIONS.REMOVE);
    handleRemoveMeal(date, mealId);
  }

  async function handleDontSuggest(date, mealId) {
    // P8b-A: persist the permanent 'Don't Suggest' exclusion to the account FIRST.
    // Only on success do we mutate the current draft (remove row/pair + temp
    // exclude). Never falsely claim a permanent exclusion if persistence failed.
    try {
      await mealAPI.addExclusion(mealId);
    } catch (err) {
      toast.error("Couldn't exclude this meal — please try again");
      return;
    }
    setExcludedMealIds(function (prev) { return prev.concat([mealId]); });
    handleRemoveMeal(date, mealId);
    toast.info("Won't suggest this again");
  }

  // Ordinary (non-linked) row: open the regular Plan Swap browser for THIS
  // draft row. The draft is kept for the return trip; the browser's choice
  // only comes back to this draft (never a meal-plan write).
  function openPlanSwap(date, mealType, row) {
    var started = startDraftSwap({
      reviewKey: location.key,
      state: {
        plans: plans, lockedMeals: lockedMeals, excludedMealIds: excludedMealIds,
        complianceByDate: complianceByDate, expandedDays: expandedDays,
      },
      request: {
        date: date,
        rows: getMealsByDate(date).map(function (m) {
          return {
            row_key: m.draft_row_key, plan_id: m.plan_id || null, meal_id: m.meal_id,
            meal_type: m.meal_type, scale_factor: m.scale_factor || 1.0,
          };
        }),
        outgoing_row_key: row.draft_row_key,
        outgoing_meal_id: row.meal_id,
        replace_plan_ids: replacePlanIds,
      },
    });
    if (!started) {
      toast.error("Couldn't open Swap. Please try again.");
      return;
    }
    navigate(buildUrl('/meals', {
      guided: 'true', date: date, type: mealType, return: currentPath(location),
      swap: 'true', outgoingMealId: row.meal_id, draftSwap: '1', draftRowKey: row.draft_row_key,
    }), { state: startFlowState() });
  }

  async function handleSwap(date, mealType, currentMealId, rowKey) {
    var row = getMealsByDate(date).find(function (m) {
      return rowKey ? m.draft_row_key === rowKey : m.meal_id === currentMealId;
    });
    var isLinkedRow = !!(row && row.leftover_role && row.leftover_group_id);
    // Draft pair-swap is only available for a VALID two-member group. Malformed
    // or single-member linked drafts must be changed from the saved plan.
    if (isLinkedRow && !isValidDraftPair(row.leftover_group_id)) {
      toast.info('This linked pair can only be changed from your saved plan — use Swap there.');
      return;
    }
    var isPair = isLinkedRow;
    if (!isPair) {
      if (row) openPlanSwap(date, mealType, row);
      return;
    }
    // Linked rows ALWAYS go through the pair-safe flow — never a one-row swap.
    var slotForAlts = isPair ? 'Dinner' : mealType;
    var pairMembers = isPair ? getGroupMembers(row.leftover_group_id) : [];
    var cookMember = pairMembers.find(function(m) { return m.leftover_role === 'cook'; });
    var leftoverMember = pairMembers.find(function(m) { return m.leftover_role === 'leftover'; });
    var rankingDate = isPair && cookMember ? cookMember.date : date;
    setSwapSlot({
      date: rankingDate, mealType: slotForAlts, currentMealId: currentMealId,
      isPair: isPair, groupId: isPair ? row.leftover_group_id : null,
    });
    setLoadingAlternatives(true);
    try {
      var dayMeals = getMealsByDate(rankingDate)
        .filter(function (m) { return isPair ? m.leftover_group_id !== row.leftover_group_id : m.meal_id !== currentMealId; })
        .map(function (m) { return { meal_id: m.meal_id, meal_type: m.meal_type, scale_factor: m.scale_factor || 1.0 }; });
      var weekMeals = plans.map(function (p) { return {
        meal_id: p.meal_id, date: p.date, meal_type: p.meal_type,
        calories: p.calories, protein: p.protein, carbs: p.carbs, fat: p.fat,
        scale_factor: p.scale_factor || 1.0, leftover_group_id: p.leftover_group_id || null,
      }; });
      var pairContext = isPair ? {
        group_id: row.leftover_group_id, cook_date: cookMember.date,
        leftover_date: leftoverMember.date, cook_scale: cookMember.scale_factor || 1.0,
        leftover_scale: leftoverMember.scale_factor || 1.0,
      } : null;
      // The recipe being swapped out is never offered as its own replacement.
      var altExcludes = excludedMealIds.concat([currentMealId]);
      var res = await smartFillAPI.alternatives(rankingDate, slotForAlts, dayMeals, weekMeals, altExcludes, 6, pairContext);
      setSwapAlternatives(res.data.alternatives || []);
    } catch (err) {
      toast.error('Failed to load alternatives');
      setSwapSlot(null);
    } finally {
      setLoadingAlternatives(false);
    }
  }

  function applySwap(alt) {
    if (!swapSlot) return;
    // P8b-B1: a completed draft swap teaches taste — outgoing negative, selected
    // replacement positive. Ordered (swap_out settles before swap_selected) so a
    // same-family swap deterministically ends positive.
    recordSwapTasteSignals(swapSlot.currentMealId, alt.meal_id);
    if (swapSlot.isPair && swapSlot.groupId) {
      // PAIR-SAFE: update BOTH linked draft rows together via the shared pure
      // reducer — same recipe on both, each row's own consumption scale + role +
      // shared group id preserved, prep_scale_factor = S1 + S2 recomputed on the
      // cook. Never one-sided. (One taste signal recorded above for the pair.)
      var pairPlans = pairLogic.applyPairSwapToPlans(plans, swapSlot.groupId, alt);
      setPlans(pairPlans);
      reevaluateDraft(pairPlans);
      setSwapSlot(null);
      setSwapAlternatives([]);
      toast.success('Both linked meals swapped');
    }
  }

  async function handleRegenerateDay(date) {
    setRegeneratingDay(date);
    try {
      // LEFTOVERS: a linked row (cook or leftover) is IMPLICITLY locked during
      // whole-day regeneration — never regenerate or exclude it, and keep it in
      // the draft so its partner on the adjacent date is never orphaned.
      var isLinkedRow = function (m) { return !!m.leftover_role; };
      // LOGGED FOOD: a row the user already logged (`is_logged`, completed) is
      // IMPLICITLY locked too — no manual Keep/Lock needed. It is sent with its
      // own plan_id + saved macros (counted once as fixed nutrition), is never
      // excluded or replaced, and stays in the draft.
      var isLoggedRow = function (m) { return !!m.is_logged; };
      var isFixedRow = function (m) { return isLocked(date, m.meal_id) || isLinkedRow(m) || isLoggedRow(m); };
      var locked = getMealsByDate(date)
        .filter(isFixedRow)
        .map(function (m) {
          return {
            plan_id: m.plan_id || null,
            date: m.date || date,
            meal_id: m.meal_id, meal_type: m.meal_type, scale_factor: m.scale_factor || 1.0,
            calories: m.calories, protein: m.protein, carbs: m.carbs, fat: m.fat,
            leftover_group_id: m.leftover_group_id || null, leftover_role: m.leftover_role || null,
            scale_label: m.scale_label || null, prep_scale_factor: m.prep_scale_factor || null,
            omitted_ingredient_ids: m.omitted_ingredient_ids || []
          };
        });
      var weekMeals = plans
        .filter(function (p) { return p.date !== date; })
        .map(function (p) {
          return {
            plan_id: p.plan_id || null,
            meal_id: p.meal_id, date: p.date, meal_type: p.meal_type,
            scale_factor: p.scale_factor || 1.0,
            calories: p.calories, protein: p.protein, carbs: p.carbs, fat: p.fat,
            // Cross-date leftover linkage so the backend can validate an adjacent
            // pair WITHOUT reconstructing data the client never sent. Send the FULL
            // pair identity on the partner row so context is symmetric with `locked`.
            leftover_group_id: p.leftover_group_id || null,
            leftover_role: p.leftover_role || null,
            scale_label: p.scale_label || null,
            prep_scale_factor: p.prep_scale_factor || null
          };
        });
      // Exclude current non-locked, non-linked, non-logged meals so the planner picks different ones
      var currentMealIds = getMealsByDate(date)
        .filter(function (m) { return !isFixedRow(m); })
        .map(function (m) { return m.meal_id; });
      var allExcluded = excludedMealIds.concat(currentMealIds);
      var res = await smartFillAPI.regenerateDay(date, locked.length > 0 ? locked : null, weekMeals, allExcluded.length > 0 ? allExcluded : null);
      if (res.data.success) {
        // Track newly excluded meal IDs for future regenerations
        setExcludedMealIds(allExcluded);
        // P8b-B1: manual Lock/Keep on a REGULAR (non-linked) row => keep +1;
        // regenerated-away regular recipes => regenerate_replaced -1. Linked rows
        // implicitly retained for pair safety and implicitly locked logged food
        // are NEUTRAL (never in currentMealIds). Ordered (negatives then
        // positives) so a same-family Keep-vs-regenerate resolves as Keep.
        var keepMealIds = getMealsByDate(date)
          .filter(function (m) { return isLocked(date, m.meal_id) && !isLinkedRow(m); })
          .map(function (m) { return m.meal_id; });
        recordOrderedRegenSignals(currentMealIds, keepMealIds);
        // New rows get their Swap row key (kept rows keep theirs).
        var regeneratedPlans = pairLogic.withDraftRowKeys((function (prev) {
          var kept = prev.filter(function (p) {
            return p.date !== date || isLocked(date, p.meal_id) || p.leftover_role || p.is_logged;
          });
          return dedupePrimarySlots(kept.concat(res.data.new_meals || []), date);
        })(plans));
        setPlans(regeneratedPlans);
        await reevaluateDraft(regeneratedPlans);
        toast.success('Day regenerated');
      } else {
        // Legitimate terminal failure (HTTP 200, success:false) — the planner
        // could not find a compliant plan for this day's open slots. This
        // must never look like the button silently did nothing.
        toast.error(friendlyRegenerateDayFailure(res.data, 'Failed to regenerate day'));
      }
    } catch (err) {
      toast.error(friendlyPlanError(err, 'Failed to regenerate day'));
    } finally {
      setRegeneratingDay(null);
    }
  }

  var weekOffset = draftData.weekOffset || 0;
  // Save / Cancel return to the exact Plan location the review was opened
  // from (Daily date or Weekly week). weekOffset is only the fallback for a
  // review state created before returnTo existed.
  var returnTo = safeReturnPath(draftData.returnTo, '/plan' + (weekOffset !== 0 ? '?week=' + weekOffset : ''));
  // Generate Week (PlanFoodLauncher) only: after a successful save, open the
  // generated week in Plan instead of the origin. Cancel always uses returnTo;
  // every other caller (e.g. PlanningHub Regenerate) omits this field.
  var saveReturnTo = safeReturnPath(draftData.saveReturnTo, null);

  // 6C: dates the planner could NOT complete On Track are authoritative from the
  // backend terminal status. incomplete (search_failed/catalog_infeasible) and any
  // legacy off_track are NOT saveable as a generated plan — the user must
  // Regenerate Day. constrained_no_feasible_addition + on_track ARE saveable.
  var blockedDates = pairLogic.computeBlockedDates(complianceByDate, dates);

  async function handleSave() {
    if (evaluatingDraft) {
      toast.info('Still verifying your updated plan.');
      return;
    }
    // Block saving an incomplete week: a selected day with zero meals must be
    // regenerated or removed first. Never silently save a partial plan.
    var incompleteDates = dates.filter(function (date) {
      return getMealsByDate(date).length === 0;
    });
    if (incompleteDates.length > 0) {
      toast.error('Some selected days have no meals yet. Regenerate or remove them before saving.');
      return;
    }
    // ON-TRACK INVARIANT: never offer to save a generated off-target / incomplete
    // day. There is NO "continue & save anyway" path.
    if (blockedDates.length > 0) {
      toast.error("Some days couldn't be completed On Track. Use Regenerate Day on them before saving.");
      return;
    }
    setSaving(true);
    // Reuse the existing attempt's op id on retry; only mint a new one if none.
    var opId = saveOpId;
    if (!opId) {
      opId = 'confirm_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
      setSaveOpId(opId);
    }
    try {
      var confirmPlans = plans.map(function (p) {
        return {
          date: p.date, meal_type: p.meal_type, meal_id: p.meal_id,
          meal_name: p.meal_name, scale_factor: p.scale_factor || 1.0,
          scale_label: p.scale_label || null,
          calories: Math.round(p.calories || 0),
          protein: Math.round(p.protein || 0),
          carbs: Math.round(p.carbs || 0),
          fat: Math.round(p.fat || 0),
          is_kept: p.is_kept || false,
          // CRITICAL for kept-meal preservation: the original DB row's
          // plan_id must round-trip through /smart-fill/confirm so the
          // backend can spare this row from its delete sweep instead of
          // wiping it and then skipping re-insert (the old bug).
          plan_id: p.plan_id || null,
          // LEFTOVERS: preserve dinner<->next-day-lunch linkage through save.
          leftover_group_id: p.leftover_group_id || null,
          leftover_role: p.leftover_role || null,
          // prep_scale_factor drives the cook card's batch ingredient amounts;
          // dropping it here made the saved cook meal render one-portion ingredients.
          prep_scale_factor: p.prep_scale_factor || null,
          // P3B: allergen-omission state (server re-validates + is authoritative).
          omitted_ingredient_ids: p.omitted_ingredient_ids || []
        };
      });
      await smartFillAPI.confirm(confirmPlans, dates, true, opId, replacePlanIds);
      setSaveOpId(null);
      toast.success('Plan saved!');
      // Signal PlanningHub to continue its Fix Days flow (advance to next day
      // in queue) — but only for the date(s) this save actually covered. The
      // consumer verifies the current queue target is among them before
      // advancing (see AGENTS.md pre-launch audit, finding FIXDAYS-1).
      try {
        var rawFix = sessionStorage.getItem(FIX_MODE_KEY);
        if (rawFix) {
          var parsed = JSON.parse(rawFix);
          if (parsed && parsed.active) {
            setFixModeAdvancePending(dates);
          }
        }
      } catch (_) { /* best-effort */ }
      if (saveReturnTo) {
        // Show the saved week; replacing the review entry so Back can't
        // reopen an already-saved draft (Back then returns to the origin).
        navigate(saveReturnTo, { replace: true });
        return;
      }
      // Back to the exact Plan location this review came from, popping the
      // review off history so Back can't reopen an already-saved draft.
      finishFlow(navigate, location, returnTo);
    } catch (err) {
      toast.error(friendlyPlanError(err, 'Failed to save plan'));
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    finishFlow(navigate, location, returnTo);
  }

  if (!plans.length) {
    return (
      <div data-testid="plan-review-empty" className="min-h-screen bg-background text-foreground flex items-center justify-center p-4">
        <div className="text-center space-y-4">
          <p className="text-muted-foreground">No meals to review. Generate a plan first.</p>
          <Button onClick={function () { finishFlow(navigate, location, returnTo); }} variant="outline">Go to Plan</Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="plan-review" className="min-h-screen bg-background text-foreground pb-32">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b border-border px-4 py-3 header-safe-top">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <button onClick={handleCancel} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors" data-testid="review-back-btn">
            <ArrowLeft size={18} />
            <span className="text-sm">Cancel</span>
          </button>
          <h1 className="text-base font-heading font-semibold">Review Plan</h1>
          <Button onClick={function() { handleSave(); }} disabled={saving || evaluatingDraft || blockedDates.length > 0} size="sm" className={(blockedDates.length > 0 || evaluatingDraft) ? "bg-muted text-muted-foreground" : "bg-accent hover:bg-accent/90"} data-testid="review-save-btn">
            {saving ? 'Saving...' : evaluatingDraft ? 'Checking...' : 'Save'}
            {!saving && !evaluatingDraft && blockedDates.length === 0 && <Check size={14} className="ml-1" />}
            {!saving && !evaluatingDraft && blockedDates.length > 0 && <AlertTriangle size={14} className="ml-1" />}
          </Button>
        </div>
      </div>

      {/* Day cards */}
      <div className="max-w-2xl mx-auto px-4 pt-4 space-y-3">
        {dates.map(function (date) {
          var dayMeals = getMealsByDate(date);
          var totals = getDayTotals(date);
          var canonical = complianceByDate[date];
          var status = canonical && (canonical.status === 'on_track' || canonical.status === 'constrained_no_feasible_addition')
            ? 'on-track' : 'needs-adjustment';
          var display = getStatusDisplay(status);
          var expanded = expandedDays[date];

          // Macro percentages for progress bars
          var calPct = dailyTarget.calories > 0 ? Math.round((totals.calories / dailyTarget.calories) * 100) : 0;
          var protPct = dailyTarget.protein > 0 ? Math.round((totals.protein / dailyTarget.protein) * 100) : 0;
          var carbsPct = dailyTarget.carbs > 0 ? Math.round((totals.carbs / dailyTarget.carbs) * 100) : 0;
          var fatPct = dailyTarget.fat > 0 ? Math.round((totals.fat / dailyTarget.fat) * 100) : 0;

          return (
            <div key={date} className={'rounded-xl border bg-card ' + display.borderColor} data-testid={'review-day-' + date}>
              {/* Day header — collapsible */}
              <button onClick={function () { toggleDay(date); }}
                className="w-full flex items-center justify-between px-4 py-3 text-left" data-testid={'review-day-toggle-' + date}>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading font-medium text-sm">{formatDate(date)}</span>
                    <span className={'text-xs font-medium px-2 py-0.5 rounded-full ' + display.bgColor + ' ' + display.textColor}
                      data-testid={'review-day-status-' + date}>
                      {display.label}
                    </span>
                    {/* 6C: authoritative planner terminal status. constrained =
                        the user's fixed/logged food already fills the day and no
                        additional meal fits the remaining targets (saveable).
                        incomplete/off_track = the planner could not complete an
                        On Track day; the user must Regenerate Day (NOT saveable,
                        and NEVER presented as an AI "best fit"). */}
                    {complianceByDate[date] && complianceByDate[date].status === 'constrained_no_feasible_addition' && (
                      <span
                        className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-600 border border-sky-500/30"
                        data-testid={'review-constrained-pill-' + date}
                        title="Your fixed and logged food already fills this day; nothing else fits your remaining targets."
                      >
                        No additional meal fits your remaining targets.
                      </span>
                    )}
                    {complianceByDate[date] && (complianceByDate[date].status === 'incomplete' || complianceByDate[date].status === 'off_track') && (
                      <span
                        className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-red-500/10 text-red-600 border border-red-500/30"
                        data-testid={'review-incomplete-pill-' + date}
                        title="The planner couldn't complete this day On Track. Tap Regenerate Day to try again."
                      >
                        Couldn't complete · Regenerate Day
                      </span>
                    )}
                  </div>
                  {/* Mini macro progress bars */}
                  <div className="flex gap-1.5 mt-2">
                    {[
                      { pct: calPct, macro: 'calories' },
                      { pct: protPct, macro: 'protein' },
                      { pct: carbsPct, macro: 'carbs' },
                      { pct: fatPct, macro: 'fat' },
                    ].map(function (item) {
                      return (
                        <div key={item.macro} className="flex-1 h-1.5 bg-secondary rounded-full overflow-hidden">
                          <div className={'h-full rounded-full ' + getMacroBarColor(item.pct, item.macro, userGoal)}
                            style={{ width: Math.min(item.pct, 100) + '%' }} />
                        </div>
                      );
                    })}
                  </div>
                </div>
                {expanded ? <ChevronUp size={16} className="text-muted-foreground ml-3" /> : <ChevronDown size={16} className="text-muted-foreground ml-3" />}
              </button>

              {/* Expanded day content */}
              {expanded && (
                <div className="px-4 pb-3 space-y-2">
                  {dayMeals.map(function (meal, idx) {
                    var locked = isLocked(date, meal.meal_id);
                    var isScaled = meal.scale_factor && meal.scale_factor !== 1.0;
                    return (
                      <div key={meal.meal_id + '_' + idx}
                        className={'rounded-lg p-3 border transition-colors ' + (locked ? 'bg-accent/5 border-accent/30' : 'bg-card border-border/40')}
                        data-testid={'review-meal-' + meal.meal_id}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider">{meal.meal_type}</span>
                              {isScaled && (
                                <span className="text-[10px] font-medium text-accent bg-accent/10 px-1.5 py-0.5 rounded flex items-center gap-0.5" data-testid={'review-scale-' + meal.meal_id}>
                                  <Scale size={9} /> {meal.scale_factor}x
                                </span>
                              )}
                              {locked && <Lock size={10} className="text-accent" />}
                            </div>
                            <p className="text-sm font-medium mt-0.5 truncate">{meal.meal_name}</p>
                            {meal.leftover_role === 'cook' && (
                              <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-medium text-accent bg-accent/10 px-1.5 py-0.5 rounded" data-testid={'review-leftover-cook-' + meal.meal_id}>
                                <Package size={9} /> Prepare dinner plus tomorrow's lunch
                              </span>
                            )}
                            {meal.leftover_role === 'leftover' && (
                              <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-medium text-muted-foreground bg-secondary/60 px-1.5 py-0.5 rounded" data-testid={'review-leftover-lunch-' + meal.meal_id}>
                                <Package size={9} /> Leftover from yesterday
                              </span>
                            )}
                            <div className="flex gap-3 mt-1 text-[11px] text-muted-foreground">
                              <span>{Math.round(meal.calories)} cal</span>
                              <span>{Math.round(meal.protein)}g P</span>
                              <span>{Math.round(meal.carbs)}g C</span>
                              <span>{Math.round(meal.fat)}g F</span>
                            </div>
                          </div>

                          {/* Primary actions: Lock + Swap | Overflow for Remove/Don't Suggest */}
                          <div className="flex items-center gap-1 shrink-0">
                            <button onClick={function () { toggleLock(date, meal.meal_id); }}
                              className={'p-1.5 rounded-md transition-colors ' + (locked ? 'bg-accent/15 text-accent' : 'text-muted-foreground hover:text-foreground hover:bg-secondary')}
                              data-testid={'review-lock-' + meal.meal_id}
                              title={locked ? 'Unlock' : 'Lock'}>
                              {locked ? <Unlock size={14} /> : <Lock size={14} />}
                            </button>
                            <button onClick={function () { handleSwap(date, meal.meal_type, meal.meal_id, meal.draft_row_key); }}
                              disabled={locked}
                              className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary disabled:opacity-30 transition-colors"
                              data-testid={'review-swap-' + meal.meal_id}
                              title="Swap">
                              <ArrowRightLeft size={14} />
                            </button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  disabled={locked}
                                  className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary disabled:opacity-30 transition-colors"
                                  data-testid={'review-more-' + meal.meal_id}>
                                  <MoreHorizontal size={14} />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-44">
                                <DropdownMenuItem
                                  onClick={function () { handleDirectRemove(date, meal.meal_id); }}
                                  data-testid={'review-remove-' + meal.meal_id}>
                                  <X size={14} className="mr-2" /> Remove
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={function () { handleDontSuggest(date, meal.meal_id); }}
                                  className="text-red-500 focus:text-red-500"
                                  data-testid={'review-dont-suggest-' + meal.meal_id}>
                                  <Ban size={14} className="mr-2" /> Don&apos;t suggest
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Day footer: target summary + regenerate */}
                  <div className="flex items-center justify-between pt-2 border-t border-border/30">
                    <div className="flex gap-3 text-[11px]">
                      <span className="text-muted-foreground">Target:</span>
                      <span className="text-muted-foreground">{dailyTarget.calories} cal</span>
                      <span className="text-muted-foreground">{dailyTarget.protein}g P</span>
                      <span className="text-muted-foreground">{dailyTarget.carbs}g C</span>
                    </div>
                    <Button onClick={function () { handleRegenerateDay(date); }}
                      disabled={regeneratingDay === date}
                      variant="ghost" size="sm" className="text-xs h-7 text-muted-foreground hover:text-foreground"
                      data-testid={'review-regen-day-' + date}>
                      <RefreshCw size={12} className={regeneratingDay === date ? 'animate-spin mr-1' : 'mr-1'} />
                      {regeneratingDay === date ? 'Regenerating...' : 'Regenerate Day'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Linked leftover pairs keep the draft pair picker (ordinary rows open the
          regular Plan Swap browser); selection mutates only local draft state. */}
      {swapSlot && (
        <DraftSwapPicker slot={swapSlot.isPair ? 'both linked meals' : swapSlot.mealType}
          alternatives={swapAlternatives} loading={loadingAlternatives} onSelect={applySwap}
          onClose={function () { setSwapSlot(null); setSwapAlternatives([]); }} />
      )}

      {/* Bottom bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur border-t border-border p-4 z-10">
        <div className="max-w-2xl mx-auto flex gap-3">
          <Button onClick={handleCancel} variant="outline" className="flex-1 border-border text-muted-foreground" data-testid="review-cancel-btn">
            Cancel
          </Button>
          <Button onClick={function() { handleSave(); }} disabled={saving || evaluatingDraft || blockedDates.length > 0} className={(blockedDates.length > 0 || evaluatingDraft) ? "flex-1 bg-muted text-muted-foreground" : "flex-1 bg-accent hover:bg-accent/90"} data-testid="review-confirm-btn">
            {saving ? 'Saving...' : evaluatingDraft ? 'Checking...' : 'Confirm & Save'}
            {!saving && blockedDates.length === 0 && <Check size={14} className="ml-1" />}
            {!saving && blockedDates.length > 0 && <AlertTriangle size={14} className="ml-1" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
